import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, Printer, Edit, FileText, Building2, User, Calendar,
  CreditCard, CheckCircle2, Clock, AlertCircle, Receipt
} from "lucide-react";
import { cn } from "@/lib/utils";
import { invoiceTaxableAmount } from "@/lib/practiceBilling";

// Currency formatter
const currency = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};

// Date formatter
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("it-IT");
  } catch {
    return dateStr;
  }
};

export function InvoiceDetail({ 
  invoice, 
  client,
  linkedCase,
  onBack, 
  onEdit, 
  onPrint,
  onAddPayment
}) {
  if (!invoice) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Fattura non trovata</p>
      </div>
    );
  }

  // Payment status
  const getPaymentStatus = () => {
    const ps = invoice.paymentStatus || "non_pagata";
    const configs = {
      pagata: { className: "bg-green-100 text-green-700", icon: CheckCircle2, label: "Pagata" },
      parziale: { className: "bg-amber-100 text-amber-700", icon: Clock, label: "Parzialmente Pagata" },
      non_pagata: { className: "bg-red-100 text-red-700", icon: AlertCircle, label: "Non Pagata" }
    };
    return configs[ps] || configs.non_pagata;
  };

  const paymentStatus = getPaymentStatus();
  const PaymentIcon = paymentStatus.icon;
  const residuo = (invoice.totale || 0) - (invoice.totalPaid || 0);

  return (
    <div className="space-y-6">
      {/* Header with Back button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack}
            className="gap-2"
            data-testid="invoice-detail-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna alla lista
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Receipt className="h-6 w-6 text-slate-600" />
              Fattura {invoice.invoiceNumber}
            </h1>
            <p className="text-sm text-slate-500">
              {invoice.managementType === "agenzia" ? "Gestione Agenzia" : "Gestione Studio"}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onPrint} className="gap-2">
            <Printer className="h-4 w-4" />
            Stampa PDF
          </Button>
          <Button variant="outline" size="sm" onClick={onEdit} className="gap-2">
            <Edit className="h-4 w-4" />
            Modifica
          </Button>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* Left column - Invoice info */}
        <Card className="md:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Dettagli Fattura
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500">Numero</p>
                <p className="font-semibold">{invoice.invoiceNumber}</p>
              </div>
              <div>
                <p className="text-slate-500">Data Fattura</p>
                <p className="font-semibold">{formatDate(invoice.invoiceDate)}</p>
              </div>
              <div>
                <p className="text-slate-500">Metodo Pagamento</p>
                <p className="font-semibold">{invoice.paymentMethod || "Non specificato"}</p>
              </div>
              <div>
                <p className="text-slate-500">Stato</p>
                <Badge className={cn("mt-1", paymentStatus.className)}>
                  <PaymentIcon className="h-3 w-3 mr-1" />
                  {paymentStatus.label}
                </Badge>
              </div>
            </div>

            <Separator />

            {/* Description */}
            {invoice.notes && (
              <div>
                <p className="text-slate-500 text-sm mb-1">Descrizione</p>
                <p className="text-sm bg-slate-50 p-3 rounded">{invoice.notes}</p>
              </div>
            )}

            {/* Linked Practice */}
            {linkedCase && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-amber-700 text-sm font-medium mb-1">Pratica Collegata</p>
                <p className="text-sm">
                  <strong>{linkedCase.caseNumber}</strong> - {linkedCase.subject}
                </p>
                {linkedCase.enteCoinvolto && (
                  <p className="text-xs text-amber-600 mt-1">Ente: {linkedCase.enteCoinvolto}</p>
                )}
              </div>
            )}

            <Separator />

            {/* Amounts breakdown */}
            <div className="space-y-2 text-sm">
              {/* TOTALE - Primary value, visually dominant */}
              <div className="bg-green-600 rounded-lg px-4 py-3 flex justify-between items-center -mx-1">
                <span className="text-white font-bold text-base">Totale Fattura (IVA inclusa)</span>
                <span className="text-white font-bold text-xl font-mono">{currency(invoice.totale)}</span>
              </div>
              
              <div className="pt-1 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Imponibile</span>
                  <span className="font-mono">{currency(invoiceTaxableAmount(invoice))}</span>
                </div>
                {invoice.contributoIntegrativo > 0 && (
                  <div className="flex justify-between text-blue-600">
                    <span>Contributo Integrativo 4%</span>
                    <span className="font-mono">{currency(invoice.contributoIntegrativo)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">IVA ({invoice.iva || 22}%)</span>
                  <span className="font-mono">{currency(invoice.ivaAmount)}</span>
                </div>
                {invoice.speseArt15 > 0 && (
                  <div className="flex justify-between text-amber-600">
                    <span>Spese Art. 15 (escluse IVA)</span>
                    <span className="font-mono">{currency(invoice.speseArt15)}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right column - Client and Payment info */}
        <div className="space-y-4">
          {/* Client Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                {client?.clientType === "Azienda" ? (
                  <Building2 className="h-4 w-4" />
                ) : (
                  <User className="h-4 w-4" />
                )}
                Cliente
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1">
              <p className="font-semibold">{client?.fullName || invoice.clientName || "N/A"}</p>
              {client?.address && <p className="text-slate-500">{client.address}</p>}
              {client?.taxCode && <p className="text-slate-500">C.F.: {client.taxCode}</p>}
              {client?.vatNumber && <p className="text-slate-500">P.IVA: {client.vatNumber}</p>}
              {client?.email && <p className="text-slate-500">{client.email}</p>}
            </CardContent>
          </Card>

          {/* Payment Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Pagamenti
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-slate-500">Incassato</p>
                  <p className="font-semibold text-green-600 font-mono">
                    {currency(invoice.totalPaid || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Residuo</p>
                  <p className={cn(
                    "font-semibold font-mono",
                    residuo > 0 ? "text-red-600" : "text-green-600"
                  )}>
                    {currency(residuo)}
                  </p>
                </div>
              </div>

              {/* Payment history */}
              {invoice.payments && invoice.payments.length > 0 && (
                <div className="space-y-2 pt-2 border-t">
                  <p className="text-xs text-slate-500 font-medium">Storico pagamenti</p>
                  {invoice.payments.map((payment, idx) => (
                    <div key={idx} className="text-xs bg-slate-50 p-2 rounded flex justify-between">
                      <span>{formatDate(payment.paymentDate)} - {payment.paymentMethod}</span>
                      <span className="font-mono">{currency(payment.amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              {residuo > 0 && (
                <Button 
                  size="sm" 
                  className="w-full mt-2"
                  onClick={onAddPayment}
                >
                  Registra Pagamento
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default InvoiceDetail;
